import type { Row, Table as TanstackTable } from '@tanstack/react-table';
import type { DealEventTct } from '@dealstack/shared/lib/backendClient';
import type { MouseEvent } from 'react';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { ShowHideColumnsButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ShowHideColumnsButton/ShowHideColumnsButton';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { DealEventDetailPanel } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventDetailPanel.tsx';
import { hasDealEventDetail } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogChanges.utils.ts';
import {
	dealEventMessageId,
	dealEventMessagePath,
	eventLogLinkState,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';

interface Props {
	dealId: string;
	columns: ExtendedColumnDef<DealEventTct>[];
	rows: DealEventTct[];
	dataOperations: MantineTableDataOperations;
	showColumnFilters: boolean;
	onShowColumnFiltersChange: (value: boolean) => void;
	isLoading?: boolean;
}

export const DealEventLogTable = ({
	dealId,
	columns,
	rows,
	dataOperations,
	showColumnFilters,
	onShowColumnFiltersChange,
	isLoading = false,
}: Props) => {
	const navigate = useNavigate();
	const { search } = useLocation();

	const canExpand = useCallback((row: Row<DealEventTct>) => hasDealEventDetail(row.original), []);

	const rowId = useCallback((event: DealEventTct) => event.id, []);

	const rowProps = useCallback(
		({ row }: { row: Row<DealEventTct> }) => {
			const messageId = dealEventMessageId(row.original);

			return {
				id: row.id,
				style: messageId || row.getCanExpand() ? { cursor: 'pointer' } : undefined,
				onClick: (event: MouseEvent<HTMLTableRowElement>) => {
					if (!messageId) {
						if (row.getCanExpand()) {
							row.toggleExpanded();
						}

						return;
					}

					const insideLink =
						event.target instanceof Element && !!event.target.closest('a');
					// A drag that picked out text was not meant as navigation.
					const selectedText = window.getSelection()?.toString();

					if (!insideLink && !selectedText) {
						navigate(dealEventMessagePath(dealId, messageId), {
							state: eventLogLinkState(search),
						});
					}
				},
			};
		},
		[dealId, navigate, search]
	);

	const detailPanel = useCallback(
		({ row }: { row: Row<DealEventTct> }) => (
			<DealEventDetailPanel event={row.original} dealId={dealId} />
		),
		[dealId]
	);

	const actions = useCallback(
		(table: TanstackTable<DealEventTct>) => (
			<ShowHideColumnsButton
				table={table}
				leftSection={<Icon variant="settings" size="vlg" />}
			/>
		),
		[]
	);

	return (
		<BasicMantineTable<DealEventTct>
			columns={columns}
			data={rows}
			isLoading={isLoading}
			dataOperations={dataOperations}
			manualFiltering
			hideSearchBar
			customActions={actions}
			toolbarClassName="deal-event-toolbar"
			enableHiding
			showColumnFilters={showColumnFilters}
			onShowColumnFiltersChange={onShowColumnFiltersChange}
			enableExpanding
			dataTestId="deal-event-log-table"
			getRowCanExpand={canExpand}
			getRowId={rowId}
			mantineTableBodyRowProps={rowProps}
			renderDetailPanel={detailPanel}
		/>
	);
};
